import { ROLES } from "~/@types/roles";
import { ProducePlanFromOrderAccess, UserRole } from "~/gql/graphqlBase.generated";

export function isUnrestrictedStaff(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN) || jwtRoles.includes(ROLES.ADMIN)) return true;
	return (myUserRoles ?? []).includes(UserRole.Admin);
}

export function shouldScopeToSalesPerson(
	user?: Maybe<SFUser>,
	myUserRoles?: UserRole[],
	hasExplicitAssignment?: boolean
): boolean {
	if (isUnrestrictedStaff(user, myUserRoles)) return false;
	return !hasExplicitAssignment;
}

export function hasUserAdminAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN)) return true;
	return (myUserRoles ?? []).includes(UserRole.Admin);
}

export function hasCommissionAdminAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN)) return true;
	const roles = myUserRoles ?? [];
	return roles.includes(UserRole.Admin) || roles.includes(UserRole.SalesManager);
}

export function hasBillingAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN)) return true;
	const roles = myUserRoles ?? [];
	return roles.includes(UserRole.Admin) || roles.includes(UserRole.Finance);
}

export function hasAnyAdminAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	return (
		hasUserAdminAccess(user, myUserRoles) ||
		hasCommissionAdminAccess(user, myUserRoles) ||
		hasBillingAccess(user, myUserRoles)
	);
}

export interface CompanySettingsAccess {
	canEnter: boolean;
	canManageCustomerNotifications: boolean;
	canManageDevices: boolean;
	canManageEmail: boolean;
	canManageSecurity: boolean;
	canManageUsers: boolean;
	canViewBilling: boolean;
	canViewGeneral: boolean;
	canViewIntegrations: boolean;
}

export function getCompanySettingsAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): CompanySettingsAccess {
	const canManageUsers = hasUserAdminAccess(user, myUserRoles);
	const canViewBilling = hasBillingAccess(user, myUserRoles);
	return {
		canEnter: canManageUsers || canViewBilling,
		canManageCustomerNotifications: canManageUsers,
		canManageDevices: canManageUsers,
		canManageEmail: canManageUsers,
		// tenantMultiFactorPolicy / setTenantMultiFactorRequirement are gated tenant-Admin server-side; mirror that here.
		canManageSecurity: canManageUsers,
		canManageUsers,
		canViewBilling,
		canViewGeneral: canManageUsers,
		canViewIntegrations: canManageUsers
	};
}

export function canViewOwnCommissions(myUserRoles?: UserRole[]): boolean {
	return (myUserRoles ?? []).includes(UserRole.Sales);
}

export function hasRestockOrdererAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN)) return true;
	const roles = myUserRoles ?? [];
	if (roles.includes(UserRole.Admin) || roles.includes(UserRole.RestockOrderer)) return true;
	return jwtRoles.includes(ROLES.RESTOCK_ORDERER);
}

export function hasEventsManagerAccess(user?: Maybe<SFUser>): boolean {
	return (user?.roles ?? []).includes(ROLES.SUPER_ADMIN);
}

export function hasProduceAdminAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (jwtRoles.includes(ROLES.SUPER_ADMIN) || jwtRoles.includes(ROLES.PRODUCE_ADMIN)) return true;
	const roles = myUserRoles ?? [];
	return roles.includes(UserRole.Admin) || roles.includes(UserRole.ProduceAdmin);
}

// Mirrors the wsapi ProductionJobCostRead policy.
export function hasCostAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	const jwtRoles = user?.roles ?? [];
	if (
		jwtRoles.includes(ROLES.SUPER_ADMIN) ||
		jwtRoles.includes(ROLES.PRODUCE_ADMIN) ||
		jwtRoles.includes(ROLES.ADMIN)
	) {
		return true;
	}
	const roles = myUserRoles ?? [];
	return roles.includes(UserRole.Admin) || roles.includes(UserRole.ProduceAdmin);
}

export function canCreatePlanFromOrder(
	user?: Maybe<SFUser>,
	myUserRoles?: UserRole[],
	access?: ProducePlanFromOrderAccess
): boolean {
	if (access !== ProducePlanFromOrderAccess.ProductionStaff) return true;
	if ((user?.roles ?? []).includes(ROLES.ADMIN) || hasProduceAdminAccess(user, myUserRoles)) return true;
	return (myUserRoles ?? []).includes(UserRole.Production);
}

export function hasVerifyScannerAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	if (isUnrestrictedStaff(user, myUserRoles)) return true;
	if ((myUserRoles ?? []).includes(UserRole.VerifyScanner)) return true;
	return (user?.roles ?? []).includes(ROLES.VERIFY_SCANNER);
}

export function hasVerifyManagerAccess(user?: Maybe<SFUser>, myUserRoles?: UserRole[]): boolean {
	if (isUnrestrictedStaff(user, myUserRoles)) return true;
	if ((myUserRoles ?? []).includes(UserRole.VerifyManager)) return true;
	return (user?.roles ?? []).includes(ROLES.VERIFY_MANAGER);
}
